import { PosturalGridSvg } from "@/components/PosturalGrid";
import { useEffect, useRef, useState, useMemo } from "react";
import BiomechanicalHeatmap from "./BiomechanicalHeatmap";

/**
 * Futuristic clinical landmark-marking canvas.
 *
 * Color system (by severity):
 *   normal & score ≥ 90  → neon blue      (aligned)
 *   normal & score < 90  → soft green     (minor deviation)
 *   moderado             → soft amber
 *   significativo        → soft red
 *
 * Props:
 *   image, landmarks, onChange, view ("front" | "side"),
 *   showPlumb, showSpine, showSkeleton,
 *   segments: { feet, knees, pelvis, spine, shoulders, cervical } from analysis
 *   plumbLine: analysis.plumb_line
 */

const FRONT_SKELETON = [
  ["shoulder_l", "shoulder_r", "shoulders"],
  ["shoulder_l", "hip_l", "spine"],
  ["shoulder_r", "hip_r", "spine"],
  ["hip_l", "hip_r", "pelvis"],
  ["hip_l", "knee_l", "knees"],
  ["knee_l", "ankle_l", "knees"],
  ["ankle_l", "foot_l", "feet"],
  ["hip_r", "knee_r", "knees"],
  ["knee_r", "ankle_r", "knees"],
  ["ankle_r", "foot_r", "feet"],
];
const SIDE_SKELETON = [
  ["ear", "shoulder", "cervical"],
  ["shoulder", "hip", "spine"],
  ["hip", "knee", "knees"],
  ["knee", "ankle", "feet"],
  ["ankle", "foot", "feet"],
];
const BACK_SKELETON = [
  ["ear_l", "occipital", "cervical"],
  ["ear_r", "occipital", "cervical"],
  ["occipital", "c7", "cervical"],
  ["shoulder_l", "shoulder_r", "shoulders"],
  ["shoulder_l", "scapula_l", "scapula"],
  ["shoulder_r", "scapula_r", "scapula"],
  ["scapula_l", "c7", "scapula"],
  ["scapula_r", "c7", "scapula"],
  ["hip_l", "hip_r", "pelvis"],
  ["shoulder_l", "hip_l", "spine"],
  ["shoulder_r", "hip_r", "spine"],
  ["hip_l", "knee_l", "knees"],
  ["knee_l", "ankle_l", "knees"],
  ["ankle_l", "heel_l", "feet"],
  ["hip_r", "knee_r", "knees"],
  ["knee_r", "ankle_r", "knees"],
  ["ankle_r", "heel_r", "feet"],
];
// Pontos esperados por vista (permite colocar manualmente os que não foram detetados com confiança).
const EXPECTED = {
  front: ["head", "shoulder_l", "shoulder_r", "hip_l", "hip_r", "knee_l", "knee_r", "ankle_l", "ankle_r", "foot_l", "foot_r"],
  side: ["ear", "shoulder", "cervical", "thoracic", "lumbar", "sacrum", "hip", "knee", "ankle", "foot"],
  back: ["head_top", "ear_l", "ear_r", "occipital", "c7", "shoulder_l", "shoulder_r", "scapula_l", "scapula_r", "thoracic", "lumbar", "sacrum",
    "hip_l", "hip_r", "knee_l", "knee_r", "ankle_l", "ankle_r", "heel_l", "heel_r"],
};
const SIDE_SPINE = ["cervical", "thoracic", "lumbar", "sacrum"];
const BACK_SPINE = ["c7", "thoracic", "lumbar", "sacrum"];

const LANDMARK_SEGMENT = {
  head: "cervical", ear: "cervical", cervical: "cervical",
  head_top: "cervical", ear_l: "cervical", ear_r: "cervical", occipital: "cervical", c7: "cervical",
  shoulder_l: "shoulders", shoulder_r: "shoulders", shoulder: "shoulders",
  scapula_l: "scapula", scapula_r: "scapula",
  thoracic: "spine", lumbar: "spine", sacrum: "spine",
  hip_l: "pelvis", hip_r: "pelvis", hip: "pelvis",
  knee_l: "knees", knee_r: "knees", knee: "knees",
  ankle_l: "feet", ankle_r: "feet", ankle: "feet",
  foot_l: "feet", foot_r: "feet", foot: "feet", heel_l: "feet", heel_r: "feet",
};

const LABELS = {
  head: "Cabeça", head_top: "Cabeça",
  shoulder_l: "Ombro E", shoulder_r: "Ombro D",
  hip_l: "Anca E", hip_r: "Anca D",
  knee_l: "Joelho E", knee_r: "Joelho D",
  ankle_l: "Tornozelo E", ankle_r: "Tornozelo D",
  foot_l: "Pé E", foot_r: "Pé D",
  ear: "Orelha", ear_l: "Orelha E", ear_r: "Orelha D",
  shoulder: "Ombro", hip: "Anca", knee: "Joelho", ankle: "Tornozelo",
  cervical: "C7", c7: "C7", occipital: "Occipital",
  thoracic: "T6", lumbar: "L3", sacrum: "Sacro",
  scapula_l: "Omoplata E", scapula_r: "Omoplata D",
  foot: "Pé (dedos)", heel_l: "Calcanhar E", heel_r: "Calcanhar D",
};

// Soft neon tokens
const NEON = {
  blue: { stroke: "rgba(56,189,248,0.85)", soft: "rgba(56,189,248,0.14)", dot: "#7dd3fc", glow: "rgba(56,189,248,0.55)" },
  green: { stroke: "rgba(52,211,153,0.82)", soft: "rgba(52,211,153,0.12)", dot: "#6ee7b7", glow: "rgba(52,211,153,0.55)" },
  amber: { stroke: "rgba(251,191,36,0.85)", soft: "rgba(251,191,36,0.14)", dot: "#fcd34d", glow: "rgba(251,191,36,0.55)" },
  red: { stroke: "rgba(244,63,94,0.88)", soft: "rgba(244,63,94,0.16)", dot: "#fda4af", glow: "rgba(244,63,94,0.6)" },
  idle: { stroke: "rgba(148,163,184,0.35)", soft: "rgba(148,163,184,0.08)", dot: "#cbd5e1", glow: "rgba(148,163,184,0.4)" },
};

function tokenFor(severity, score) {
  if (severity == null) return NEON.idle;
  if (severity === "normal") return (score ?? 100) >= 90 ? NEON.blue : NEON.green;
  if (severity === "moderado") return NEON.amber;
  if (severity === "significativo") return NEON.red;
  return NEON.idle;
}

export default function LandmarkCanvas({
  image,
  landmarks,
  onChange,
  view,
  showPlumb = false,
  showSpine = true,
  showSkeleton = true,
  segments,
  plumbLine,
  interactive = true,
  showGrid = false,
  gridSize = 32,        // px (CSS)
  gridOpacity = 0.18,   // 0..1
  gridColumns = null,   // Portal: n.º fixo de colunas (mesmo aspeto em qualquer tamanho)
  heatmap = false,      // Fase 4 — overlay biomecânico semi-radiológico (default off)
  focusMode = false,    // Polish — vignette/edge-fade subtil (default off)
  sagittal = null,      // resultado de computeSagittal (vista lateral)
}) {
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [dragging, setDragging] = useState(null);
  const [placing, setPlacing] = useState(null);
  const uid = useMemo(() => Math.random().toString(36).slice(2, 8), []);

  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      setDims({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, []);

  const pointerMove = (e) => {
    if (!dragging || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    const nx = Math.max(0, Math.min(1, x));
    const ny = Math.max(0, Math.min(1, y));
    onChange({ ...landmarks, [dragging]: { x: nx, y: ny } });
  };
  const pointerUp = () => setDragging(null);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener("pointermove", pointerMove);
    window.addEventListener("pointerup", pointerUp);
    return () => {
      window.removeEventListener("pointermove", pointerMove);
      window.removeEventListener("pointerup", pointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, landmarks]);

  const skeleton = view === "front" ? FRONT_SKELETON : (view === "side" ? SIDE_SKELETON : BACK_SKELETON);
  const lm = landmarks || {};

  const getPx = (k) => {
    const p = lm[k]; if (!p) return null;
    return { x: p.x * dims.w, y: p.y * dims.h };
  };

  const segToken = (segKey) => {
    const seg = segments?.[segKey];
    return tokenFor(seg?.severity, seg?.score);
  };

  // Spine smooth path
  const spineData = useMemo(() => {
    if (!showSpine) return null;
    if (view === "side") {
      const pts = SIDE_SPINE.map(getPx).filter(Boolean);
      if (pts.length < 2) return null;
      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1], cur = pts[i];
        const mx = (prev.x + cur.x) / 2, my = (prev.y + cur.y) / 2;
        d += ` Q ${prev.x} ${prev.y} ${mx} ${my}`;
      }
      d += ` T ${pts[pts.length - 1].x} ${pts[pts.length - 1].y}`;
      return d;
    }
    if (view === "back") {
      const pts = BACK_SPINE.map(getPx).filter(Boolean);
      if (pts.length < 2) return null;
      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 1; i < pts.length; i++) d += ` L ${pts[i].x} ${pts[i].y}`;
      return d;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lm, dims, view, showSpine]);

  const sagOk = sagittal?.available && view === "side";
  const plumbAnchor = showPlumb && view === "side" ? (sagOk ? { x: sagittal.refX * dims.w } : getPx("ankle")) : null;
  const missing = interactive ? (EXPECTED[view] || []).filter((k) => !lm[k]) : [];
  const place = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    onChange({ ...landmarks, [placing]: { x: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)), y: Math.max(0, Math.min(1, (e.clientY - r.top) / r.height)) } });
    setPlacing(null);
  };
  const plumbStatus = plumbLine?.aligned === true
    ? "normal"
    : (plumbLine?.deviations?.some(d => d.status === "significativo") ? "significativo"
      : plumbLine?.deviations?.some(d => d.status === "moderado") ? "moderado"
      : plumbLine ? "normal" : null);
  const plumbToken = plumbStatus === null ? NEON.idle :
    plumbStatus === "significativo" ? NEON.red :
    plumbStatus === "moderado" ? NEON.amber : NEON.blue;

  // Unique SVG IDs
  const gradId = (s) => `grad-${uid}-${s}`;
  const filterId = `glow-${uid}`;

  return (
    <>
    <div
      ref={wrapRef}
      className="relative rounded-2xl border border-slate-800/80 bg-[#040712] overflow-hidden select-none touch-pan-y"
      style={{
        aspectRatio: "3 / 4",
        boxShadow: "inset 0 0 80px rgba(2,6,23,0.85), 0 0 0 1px rgba(56,189,248,0.05)",
      }}
      data-testid={`canvas-${view}`}
    >
      {image ? (
        <img src={image} alt={view} className="w-full h-full object-contain absolute inset-0 pointer-events-none opacity-95" draggable={false} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-slate-600 text-sm">Sem imagem</div>
      )}

      {/* subtle scanline / vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background:
          "radial-gradient(120% 90% at 50% 40%, transparent 55%, rgba(2,6,23,0.55) 100%)",
      }} />

      {/* Grid layer — background reference (BELOW landmarks). */}
      {showGrid && dims.w > 0 && dims.h > 0 && (
        <PosturalGridSvg testId={`grid-${view}`} size={gridColumns ? dims.w / gridColumns : gridSize} opacity={gridOpacity} />
      )}

      {/* Heatmap V3 — interpretação biomecânica DESLOCADA para componente
          separado (`VirtualBiomechanicalMap`) abaixo da foto. A fotografia
          real fica reservada a observação objetiva (landmarks · plumb · linhas).
          Mantemos o componente legado importado para compatibilidade futura,
          mas não o renderizamos sobre a imagem. */}

      {/* Focus Mode V2 — "Clinical White Cloud". Centro nítido, periferia
          branco-leitosa e desfocada. CSS-only · sem segmentação · sem custo
          computacional perceptível. Fallback: imagem permanece visível mesmo
          se backdrop-filter não for suportado (a máscara apenas remove o tint). */}
      {focusMode && (
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden
          data-testid="focus-mode-overlay"
          style={{
            zIndex: 1,
            background: "radial-gradient(ellipse 60% 80% at 50% 50%, transparent 32%, rgba(241,245,249,0.55) 72%, rgba(248,250,252,0.78) 100%)",
            backdropFilter: "blur(10px) saturate(0.82)",
            WebkitBackdropFilter: "blur(10px) saturate(0.82)",
            maskImage: "radial-gradient(ellipse 55% 75% at 50% 50%, transparent 30%, black 78%)",
            WebkitMaskImage: "radial-gradient(ellipse 55% 75% at 50% 50%, transparent 30%, black 78%)",
          }}
        />
      )}

      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ mixBlendMode: "screen", zIndex: 2 }}>
        <defs>
          {/* gradients per severity — light in middle, fade at ends */}
          {Object.entries(NEON).map(([k, t]) => (
            <linearGradient key={k} id={gradId(k)} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={t.stroke} stopOpacity="0.15" />
              <stop offset="50%" stopColor={t.stroke} stopOpacity="1" />
              <stop offset="100%" stopColor={t.stroke} stopOpacity="0.15" />
            </linearGradient>
          ))}
          {/* plumb-beam vertical gradient */}
          <linearGradient id={`plumb-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={plumbToken.stroke} stopOpacity="0" />
            <stop offset="18%" stopColor={plumbToken.stroke} stopOpacity="0.45" />
            <stop offset="82%" stopColor={plumbToken.stroke} stopOpacity="0.45" />
            <stop offset="100%" stopColor={plumbToken.stroke} stopOpacity="0" />
          </linearGradient>
          {/* subtle glow filter */}
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* spine gradient */}
          <linearGradient id={`spine-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={segToken("spine").stroke} stopOpacity="0.3" />
            <stop offset="50%" stopColor={segToken("spine").stroke} stopOpacity="1" />
            <stop offset="100%" stopColor={segToken("spine").stroke} stopOpacity="0.3" />
          </linearGradient>
        </defs>

        {/* Skeleton lines */}
        {showSkeleton && skeleton.map(([a, b, seg], i) => {
          const pa = getPx(a); const pb = getPx(b);
          if (!pa || !pb) return null;
          const tok = segToken(seg);
          const tokKey = tok === NEON.blue ? "blue" : tok === NEON.green ? "green" : tok === NEON.amber ? "amber" : tok === NEON.red ? "red" : "idle";
          return (
            <g key={i}>
              {/* soft outer halo */}
              <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y}
                stroke={tok.stroke} strokeOpacity="0.22" strokeWidth={8}
                strokeLinecap="round" filter={`url(#${filterId})`} />
              {/* thin sharp line with gradient */}
              <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y}
                stroke={`url(#${gradId(tokKey)})`} strokeOpacity="0.95" strokeWidth={1.25}
                strokeLinecap="round" />
            </g>
          );
        })}

        {/* Spine curve */}
        {spineData && (
          <g>
            <path d={spineData} fill="none" stroke={segToken("spine").stroke}
              strokeOpacity="0.20" strokeWidth={10} strokeLinecap="round"
              filter={`url(#${filterId})`} />
            <path d={spineData} fill="none" stroke={`url(#spine-${uid})`}
              strokeWidth={1.6} strokeLinecap="round" />
          </g>
        )}

        {/* Plumb line — soft light beam */}
        {plumbAnchor && (
          <g>
            <rect x={plumbAnchor.x - 18} y={0} width={36} height={dims.h}
              fill={`url(#plumb-${uid})`} opacity="0.35" filter={`url(#${filterId})`} />
            <line x1={plumbAnchor.x} y1={0} x2={plumbAnchor.x} y2={dims.h}
              stroke={plumbToken.stroke} strokeOpacity="0.7" strokeWidth={0.8}
              strokeDasharray="2 6" />
          </g>
        )}

        {/* Desvios sagitais estimados: traço horizontal de cada referência até à linha */}
        {sagOk && plumbAnchor && sagittal.points.map((p) => (
          <line key={`sag-${p.key}`} x1={p.x * dims.w} y1={p.y * dims.h} x2={plumbAnchor.x} y2={p.y * dims.h} data-testid={`sagittal-tick-${p.key}`}
            stroke={p.category === "acentuado" ? NEON.red.stroke : p.category === "moderado" ? NEON.amber.stroke : p.category === "ligeiro" ? NEON.green.stroke : NEON.blue.stroke}
            strokeWidth={1.4} strokeDasharray="3 3" opacity="0.9" />
        ))}

        {/* Landmark dots — tiny glowing pips */}
        {Object.entries(lm).map(([k, p]) => {
          const segKey = LANDMARK_SEGMENT[k];
          const tok = segKey ? segToken(segKey) : NEON.blue;
          const cx = p.x * dims.w, cy = p.y * dims.h;
          const active = dragging === k;
          return (
            <g key={`dot-${k}`} style={{ pointerEvents: "none" }}>
              <circle cx={cx} cy={cy} r={active ? 10 : 7} fill={tok.stroke} opacity="0.18" filter={`url(#${filterId})`} />
              <circle cx={cx} cy={cy} r={active ? 4 : 3} fill={tok.dot} opacity="0.95" />
              <circle cx={cx} cy={cy} r={active ? 5.5 : 4.5} fill="none" stroke={tok.stroke} strokeWidth={0.8} opacity="0.8" />
            </g>
          );
        })}
      </svg>

      {/* Invisible hit-areas for dragging (above SVG for pointer) */}
      {interactive && Object.entries(lm).map(([k, p]) => (
        <button
          key={k}
          type="button"
          data-testid={`landmark-${view}-${k}`}
          aria-label={LABELS[k] || k}
          title={LABELS[k] || k}
          className={`absolute rounded-full -translate-x-1/2 -translate-y-1/2 cursor-grab touch-none transition-transform ${dragging === k ? "scale-110" : ""}`}
          style={{
            left: `${p.x * 100}%`,
            top: `${p.y * 100}%`,
            width: 26,
            height: 26,
            background: "transparent",
            border: "none",
          }}
          onPointerDown={(e) => { e.preventDefault(); setDragging(k); }}
        />
      ))}

      <div className="absolute bottom-3 left-3 flex items-center gap-2 text-[11px] px-3 py-1 rounded-full bg-slate-950/70 backdrop-blur border border-slate-800/80 text-slate-400 font-mono">
        <span className="w-1.5 h-1.5 rounded-full bg-sky-400" style={{ boxShadow: "0 0 6px rgba(56,189,248,0.9)" }} />
        {view === "front" ? "Vista frontal" : view === "side" ? "Vista lateral" : "Vista posterior"} · {Object.keys(lm).length}{EXPECTED[view] ? `/${EXPECTED[view].length}` : ""} pontos
      </div>
      {placing && (
        <div className="absolute inset-0 cursor-crosshair" style={{ zIndex: 5 }} onPointerDown={(e) => { e.preventDefault(); place(e); }} data-testid={`landmark-place-overlay-${view}`}>
          <div className="absolute top-3 left-3 right-3 text-center text-xs rounded-full bg-slate-950/80 border border-sky-500/40 text-sky-200 px-3 py-1.5">Toque na fotografia para colocar: {LABELS[placing] || placing}</div>
        </div>
      )}
    </div>
    {missing.length > 0 && (
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`landmark-missing-${view}`}>
        <span className="text-amber-300">Não detetados com confiança suficiente — colocar manualmente:</span>
        {missing.map((k) => (
          <button key={k} type="button" onClick={() => setPlacing(placing === k ? null : k)} data-testid={`landmark-place-${view}-${k}`}
            className={`rounded-full border px-2 py-0.5 transition-colors ${placing === k ? "border-sky-400 text-sky-200 bg-sky-500/10" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>{LABELS[k] || k}</button>
        ))}
      </div>
    )}
    </>
  );
}
